import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useLocalStore, useObserver } from 'mobx-react-lite';
import styled from 'styled-components/macro';
import fadeIn from '../keyframes/fadeIn';
import zoomIn from '../keyframes/zoomIn';
import { ModalProps } from '../types/ModalProps';

let modalIdCtr = 0;

const UnstyledModal: React.FC<ModalProps> = ({
  className,
  children,
  open,
  header,
  footer,
  onBackdropClick,
}) => {
  const state = useLocalStore(() => ({
    id: 'ww-modal-' + modalIdCtr++,
  }));
  const host = document?.getElementById(state.id) ?? null;

  useEffect(() => {
    if (!host) {
      const container = document.createElement('div');
      const body = document.getElementsByTagName('body')[0];
      container.id = state.id;
      body.appendChild(container);
    }
  }, [state.id, host]);

  return useObserver(() =>
    !open || !host
      ? null
      : createPortal(
          <div className={'ww-modal ' + (className ?? '')}>
            <div className="backdrop" onClick={onBackdropClick} />
            <div className="dialog">
              {header && <div className="header">{header}</div>}
              <div className="content">{children}</div>
              {footer && <div className="footer">{footer}</div>}
            </div>
          </div>,
          host,
        ),
  );
};

const Modal = styled(UnstyledModal)`
  position: fixed;
  z-index: 999000;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  overflow-y: auto;
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  justify-content: center;

  .backdrop {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: rgba(0, 0, 0, 0.75);
    opacity: 0;
    animation: ${fadeIn} 200ms ease-out forwards;
  }

  .dialog {
    position: relative;
    flex: auto;
    margin: 1em;
    background-color: var(--white);
    border-radius: 0.308em;
    max-width: 46.154em;
    box-shadow: 0px 3px 5px -1px rgba(0, 0, 0, 0.2),
      0px 6px 10px 0px rgba(0, 0, 0, 0.14), 0px 1px 18px 0px rgba(0, 0, 0, 0.12);
    opacity: 0;
    transform: scale(1.1, 1.1);
    animation: ${zoomIn} 200ms 200ms ease-out forwards;
  }

  .dialog > .header {
    font-size: 1.17em;
    font-weight: bold;
  }

  .dialog > .header,
  .dialog > .content,
  .dialog > .footer {
    padding: 1em;
  }

  .dialog > .header,
  .dialog > .footer {
    background-color: var(--white);
  }

  .dialog > *:first-child {
    border-top-left-radius: 0.308em;
    border-top-right-radius: 0.308em;
  }

  .dialog > *:last-child {
    border-bottom-left-radius: 0.308em;
    border-bottom-right-radius: 0.308em;
  }

  .dialog > .content {
    background-color: var(--gray-1);
  }

  @media (min-width: 569px) {
    .dialog {
      margin: 4em;
    }
  }
`;

export default Modal;
